import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PREVIEW_API_UNREACHABLE_EVENT,
  looksFrozen,
  previewHealthUrl,
  previewWakeConfig,
  serviceState,
  type PreviewServiceStatus,
  type PreviewWakeConfig,
  type ServiceState,
} from '../utils/previewWake';

const POLL_MS = 4000;
const SLOW_AFTER_S = 90;
const WAKE_RETRY_MS = 5000;
// A running app only gives way to the splash if the api is still down this
// long after the first failed check, so a brief network drop does not replace
// the page (and lose unsaved input).
const RECHECK_MS = 3000;

const SERVICE_LABELS: Record<string, string> = {
  api: 'App server',
  'temporal-worker': 'Background jobs',
  'temporal-worker-5m': 'Background jobs, fast queue',
  'temporal-worker-reporting': 'Reports',
  'event-relay': 'Event relay',
};
const SERVICE_ORDER = Object.keys(SERVICE_LABELS);

const PILL_STYLES: Record<ServiceState, string> = {
  waiting: 'border-border bg-muted text-muted-foreground',
  starting: 'border-amber-300 bg-amber-50 text-amber-700',
  running: 'border-green-200 bg-green-50 text-green-700',
};
const PILL_LABELS: Record<ServiceState, string> = {
  waiting: 'Waiting',
  starting: 'Starting',
  running: 'Running',
};

type Phase = 'probing' | 'alive' | 'waking' | 'blocked';
type Health = 'up' | 'down' | 'other';

async function checkHealth(): Promise<Health> {
  try {
    const response = await fetch(previewHealthUrl(), {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (response.ok) return 'up';
    return looksFrozen(response.status) ? 'down' : 'other';
  } catch {
    return 'down';
  }
}

function formatElapsed(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

interface PreviewWakeGuardProps {
  children: React.ReactNode;
  /** Called once the api answers again. Tests replace the page reload. */
  onAwake?: () => void;
}

/**
 * Shows a wake-up splash instead of the app when this preview's api has been
 * frozen for being idle, asks the thaw URL to start it, and reloads once it
 * answers. Renders children untouched on every non-preview build.
 */
export const PreviewWakeGuard: React.FC<PreviewWakeGuardProps> = ({
  children,
  onAwake,
}) => {
  const config = previewWakeConfig();
  if (!config) return <>{children}</>;
  return (
    <ActivePreviewWakeGuard config={config} onAwake={onAwake}>
      {children}
    </ActivePreviewWakeGuard>
  );
};

const ActivePreviewWakeGuard: React.FC<
  PreviewWakeGuardProps & { config: PreviewWakeConfig }
> = ({ children, config, onAwake }) => {
  const [phase, setPhase] = useState<Phase>('probing');
  const [services, setServices] = useState<PreviewServiceStatus[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const [wakeFailed, setWakeFailed] = useState(false);
  const probing = useRef(false);

  // Probe before the app mounts: a frozen api would otherwise fail the auth
  // check and send the reviewer to /login. Mid-session probes confirm a
  // failure with a second check before replacing the running app.
  const probe = useCallback(async (confirm: boolean) => {
    if (probing.current) return;
    probing.current = true;
    let health = await checkHealth();
    if (confirm && health === 'down') {
      await new Promise(resolve => window.setTimeout(resolve, RECHECK_MS));
      health = await checkHealth();
    }
    probing.current = false;
    setPhase(current =>
      health === 'down' ? 'waking' : current === 'probing' ? 'alive' : current
    );
  }, []);

  useEffect(() => {
    void probe(false);
  }, [probe]);

  // A mid-session call that fails like a frozen api triggers a fresh probe,
  // so one dropped request does not replace the page with the splash.
  useEffect(() => {
    if (phase !== 'alive') return;
    const handler = () => void probe(true);
    window.addEventListener(PREVIEW_API_UNREACHABLE_EVENT, handler);
    return () =>
      window.removeEventListener(PREVIEW_API_UNREACHABLE_EVENT, handler);
  }, [phase, probe]);

  const { stage, thawUrl } = config;

  // Ask for the thaw once per wake; retry once on a transport failure.
  useEffect(() => {
    if (phase !== 'waking') return;
    let cancelled = false;
    let retryId: number | undefined;
    const requestWake = async (attempt: number): Promise<void> => {
      try {
        const response = await fetch(thawUrl, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ stage }),
        });
        if (cancelled) return;
        if (response.status === 403) {
          setPhase('blocked');
          return;
        }
        // 429 means someone else already woke it; keep polling either way.
        if (response.ok || response.status === 429) return;
        throw new Error(`thaw answered ${response.status}`);
      } catch (error) {
        if (cancelled) return;
        if (attempt === 0) {
          retryId = window.setTimeout(() => void requestWake(1), WAKE_RETRY_MS);
          return;
        }
        console.error('Preview wake request failed', error);
        setWakeFailed(true);
      }
    };
    void requestWake(0);
    return () => {
      cancelled = true;
      window.clearTimeout(retryId);
    };
  }, [phase, stage, thawUrl]);

  // Poll the checklist and the api until the api answers, then stop.
  useEffect(() => {
    if (phase !== 'waking') return;
    let cancelled = false;
    setElapsed(0);
    const poll = async () => {
      try {
        const response = await fetch(
          `${thawUrl}?stage=${encodeURIComponent(stage)}`,
          { cache: 'no-store' }
        );
        if (response.ok && !cancelled) {
          const body = (await response.json()) as {
            services?: PreviewServiceStatus[];
          };
          setServices(body.services ?? []);
        }
      } catch {
        // The checklist is informational; the health poll decides.
      }
      if (!cancelled && (await checkHealth()) === 'up' && !cancelled) {
        cancelled = true;
        window.clearInterval(pollId);
        (onAwake ?? (() => window.location.reload()))();
      }
    };
    const pollId = window.setInterval(() => void poll(), POLL_MS);
    void poll();
    const tickId = window.setInterval(() => setElapsed(s => s + 1), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      window.clearInterval(tickId);
    };
  }, [phase, stage, thawUrl, onAwake]);

  if (phase === 'alive') return <>{children}</>;
  if (phase === 'probing') {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  return (
    <PreviewWakeSplash
      stage={stage}
      blocked={phase === 'blocked'}
      slow={elapsed >= SLOW_AFTER_S}
      wakeFailed={wakeFailed}
      elapsed={elapsed}
      services={services}
    />
  );
};

interface PreviewWakeSplashProps {
  stage: string;
  blocked: boolean;
  slow: boolean;
  wakeFailed: boolean;
  elapsed: number;
  services: PreviewServiceStatus[];
}

const PreviewWakeSplash: React.FC<PreviewWakeSplashProps> = ({
  stage,
  blocked,
  slow,
  wakeFailed,
  elapsed,
  services,
}) => {
  const byName = new Map(services.map(s => [s.name, s]));
  const prNumber = stage.replace(/^pr-/, '');

  let title = 'Waking up this preview';
  let message =
    'Previews sleep until someone opens them, and again after an hour with no visitors. This page reloads on its own when it is ready.';
  if (blocked) {
    title = 'This preview is paused';
    message =
      'Automatic wake is not switched on for this preview yet. Push a commit to the pull request to restart it.';
  } else if (wakeFailed) {
    title = 'Could not wake this preview';
    message =
      'The wake request did not go through. Push a commit to the pull request to restart it, or reload to try again.';
  } else if (slow) {
    title = 'Still waking up this preview';
    message =
      'Some previews take up to three minutes. Keep this tab open, it reloads on its own.';
  }

  return (
    <div className='relative min-h-screen overflow-hidden bg-background'>
      {/* A still, blurred app shell keeps the reviewer's sense of place. */}
      <div
        aria-hidden='true'
        className='absolute inset-0 grid grid-cols-[4rem_1fr] md:grid-cols-[14rem_1fr] opacity-50 blur-[3px]'
      >
        <div className='bg-muted' />
        <div className='flex flex-col gap-4 p-6'>
          <div className='h-5 w-48 rounded bg-muted' />
          <div className='grid grid-cols-3 gap-4'>
            <div className='h-20 rounded-lg bg-muted' />
            <div className='h-20 rounded-lg bg-muted' />
            <div className='h-20 rounded-lg bg-muted' />
          </div>
          <div className='h-3 w-full rounded bg-muted' />
          <div className='h-3 w-11/12 rounded bg-muted' />
          <div className='h-3 w-4/5 rounded bg-muted' />
        </div>
      </div>

      <div className='relative flex min-h-screen items-center justify-center p-4'>
        <div
          role='status'
          aria-live='polite'
          className='w-full max-w-md rounded-xl border bg-background p-5 shadow-lg'
        >
          <div className='flex items-center gap-3'>
            {!blocked && !wakeFailed && (
              <div className='h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-primary/30 border-t-primary' />
            )}
            <h1 className='text-base font-semibold'>{title}</h1>
          </div>
          <p className='mt-2 text-sm text-muted-foreground'>{message}</p>

          {!blocked && (
            <ul className='mt-4 divide-y rounded-lg border'>
              {SERVICE_ORDER.map(name => {
                const service = byName.get(name);
                const state = service ? serviceState(service) : 'waiting';
                return (
                  <li
                    key={name}
                    className='flex items-center justify-between gap-3 px-3 py-2 text-sm'
                  >
                    <span>{SERVICE_LABELS[name]}</span>
                    <span
                      className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${PILL_STYLES[state]}`}
                    >
                      {PILL_LABELS[state]}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <div className='mt-3 flex flex-wrap gap-x-4 text-xs text-muted-foreground'>
            {!blocked && <span>{formatElapsed(elapsed)} elapsed</span>}
            <span>PR #{prNumber}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
